import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Divider, Tooltip } from "@chakra-ui/react";
import { COLUMN_TYPE_OPTIONS, MAX_COLUMNS } from "./column-types";
import type { ColumnConfig, QuoteColumnType } from "./column-types";
import { ColumnContextSubMenu } from "./ColumnContextSubMenu";

export interface ColumnContextMenuProps {
	anchorPosition: { x: number; y: number };
	column: ColumnConfig;
	columnCount: number;
	columnIndex: number;
	isOpen: boolean;
	getDisabledReason: (type: QuoteColumnType) => string;
	isTypeDisabled: (type: QuoteColumnType, currentType: QuoteColumnType) => boolean;
	/** Optional filter — types where this returns true are omitted entirely. */
	isTypeHidden?: (type: QuoteColumnType, currentType: QuoteColumnType) => boolean;
	onChangeType: (type: QuoteColumnType) => void;
	onClose: () => void;
	onDelete: () => void;
	onDuplicate: () => void;
	onEdit: () => void;
	onInsertLeft: () => void;
	onInsertRight: () => void;
	onMoveLeft: () => void;
	onMoveRight: () => void;
}

const MENU_WIDTH = 200;
const SUBMENU_WIDTH = 220;

interface MenuItemProps {
	dangerous?: boolean;
	disabled?: boolean;
	label: React.ReactNode;
	rightIcon?: React.ReactNode;
	testId?: string;
	tooltip?: string;
	onMouseEnter?: () => void;
	onSelect?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({
	label,
	onSelect,
	disabled,
	dangerous,
	rightIcon,
	onMouseEnter,
	tooltip,
	testId,
}) => {
	const content = (
		<Box
			as="div"
			data-testid={testId}
			role="menuitem"
			aria-disabled={disabled ? true : undefined}
			onClick={() => {
				if (!disabled) onSelect?.();
			}}
			onMouseEnter={onMouseEnter}
			px={3}
			py={2}
			cursor={disabled ? "not-allowed" : "pointer"}
			opacity={disabled ? 0.4 : 1}
			color={dangerous ? "red.500" : "gray.800"}
			_hover={disabled ? undefined : { bg: "gray.50" }}
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			fontSize="sm"
		>
			<span>{label}</span>
			{rightIcon}
		</Box>
	);
	if (tooltip) {
		return (
			<Tooltip label={tooltip} placement="right">
				<span>{content}</span>
			</Tooltip>
		);
	}
	return content;
};

/**
 * Right-click context menu for column headers. Portal-rendered to document.body
 * to escape the parent modal's stacking/z-index context. Anchored to a screen-
 * space (x, y) point from the originating onContextMenu event.
 *
 * Dismiss: click outside the menu or press Escape. Escape calls
 * `event.stopPropagation()` so the parent Chakra Modal does not also close.
 */
export const ColumnContextMenu = (props: ColumnContextMenuProps) => {
	const {
		anchorPosition,
		column,
		columnIndex,
		columnCount,
		isOpen,
		onClose,
		onEdit,
		onChangeType,
		onDuplicate,
		onInsertLeft,
		onInsertRight,
		onMoveLeft,
		onMoveRight,
		onDelete,
		isTypeDisabled,
		isTypeHidden,
		getDisabledReason,
	} = props;

	const menuRef = useRef<HTMLDivElement>(null);

	// Click-outside dismiss.
	useEffect(() => {
		if (!isOpen) return undefined;
		const handler = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [isOpen, onClose]);

	// Escape dismiss with stopPropagation so the parent Modal doesn't also close.
	useEffect(() => {
		if (!isOpen) return undefined;
		const handler = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				onClose();
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [isOpen, onClose]);

	const runAndClose = useCallback(
		(fn: () => void) => () => {
			fn();
			onClose();
		},
		[onClose]
	);

	if (!isOpen) return null;
	if (typeof document === "undefined") return null;

	// Viewport-edge flip.
	const viewportWidth =
		typeof window !== "undefined" ? window.innerWidth : 1024;
	const viewportHeight =
		typeof window !== "undefined" ? window.innerHeight : 768;
	const left =
		anchorPosition.x + MENU_WIDTH > viewportWidth
			? Math.max(0, viewportWidth - MENU_WIDTH - 8)
			: anchorPosition.x;
	const top =
		anchorPosition.y + 280 > viewportHeight
			? Math.max(0, viewportHeight - 280 - 8)
			: anchorPosition.y;

	// Flip submenus to the left side of the parent menu if rendering them on
	// the right would overflow the viewport.
	const submenuFlipLeft =
		left + MENU_WIDTH + SUBMENU_WIDTH > viewportWidth;

	const moveLeftDisabled = columnIndex === 0;
	const moveRightDisabled = columnIndex >= columnCount - 1;
	const deleteDisabled = columnCount <= 1;
	// Duplicate is blocked when the table is full, or when the source column's
	// type has a per-table cap that would be exceeded (e.g. unit_of_measurement,
	// response_quantity, total — all max 1). We ask isTypeDisabled with no
	// currentColType so it evaluates as if adding a fresh column of that type.
	const duplicateTypeBlocked = isTypeDisabled(column.columnType, undefined);
	const duplicateDisabled =
		columnCount >= MAX_COLUMNS || duplicateTypeBlocked;
	const duplicateTooltip = duplicateTypeBlocked
		? getDisabledReason(column.columnType) ||
		"Only one column of this type is allowed per table"
		: undefined;

	const menu = (
		<Box
			ref={menuRef}
			role="menu"
			data-testid="column-context-menu"
			position="fixed"
			left={`${left}px`}
			top={`${top}px`}
			zIndex={9999}
			bg="white"
			boxShadow="lg"
			borderRadius="md"
			border="1px solid"
			borderColor="gray.200"
			minW={`${MENU_WIDTH}px`}
			py={1}
		>
			<MenuItem label="Edit Header" onSelect={runAndClose(onEdit)} testId="ctx-edit" />
			<MenuItem
				label="Copy Column"
				onSelect={runAndClose(onDuplicate)}
				disabled={duplicateDisabled}
				tooltip={duplicateTooltip}
				testId="ctx-duplicate"
			/>
			<ColumnContextSubMenu
				label="Change Type"
				testId="ctx-change-type"
				flipLeft={submenuFlipLeft}
			>
				{COLUMN_TYPE_OPTIONS
					.filter((opt) => !isTypeHidden?.(opt.value, column.columnType))
					.map((opt) => {
					const disabled = isTypeDisabled(opt.value, column.columnType);
					return (
						<MenuItem
							key={opt.value}
							testId={`ctx-type-${opt.value}`}
							label={opt.label}
							disabled={disabled}
							tooltip={disabled ? getDisabledReason(opt.value) : undefined}
							onSelect={runAndClose(() => onChangeType(opt.value))}
						/>
					);
				})}
			</ColumnContextSubMenu>
			<ColumnContextSubMenu
				label="Insert"
				testId="ctx-insert"
				flipLeft={submenuFlipLeft}
			>
				<MenuItem
					label="Insert Left"
					onSelect={runAndClose(onInsertLeft)}
					testId="ctx-insert-left"
				/>
				<MenuItem
					label="Insert Right"
					onSelect={runAndClose(onInsertRight)}
					testId="ctx-insert-right"
				/>
			</ColumnContextSubMenu>
			<ColumnContextSubMenu
				label="Move"
				testId="ctx-move"
				flipLeft={submenuFlipLeft}
			>
				<MenuItem
					label="Move Left"
					onSelect={runAndClose(onMoveLeft)}
					disabled={moveLeftDisabled}
					testId="ctx-move-left"
				/>
				<MenuItem
					label="Move Right"
					onSelect={runAndClose(onMoveRight)}
					disabled={moveRightDisabled}
					testId="ctx-move-right"
				/>
			</ColumnContextSubMenu>
			<Divider my={1} />
			<MenuItem
				label="Delete Column"
				onSelect={runAndClose(onDelete)}
				disabled={deleteDisabled}
				dangerous
				testId="ctx-delete"
				tooltip={deleteDisabled ? "At least one column is required" : undefined}
			/>
		</Box>
	);

	return createPortal(menu, document.body);
};

export default ColumnContextMenu;
